import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowLeft } from 'lucide-react';

const Cart = () => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSessionId = () => {
    let sessionId = localStorage.getItem('cart_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cart_session_id', sessionId);
    }
    return sessionId;
  };

  const loadCart = async () => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_URL}/api/store/cart/${sessionId}`);
      const data = await response.json();
      setCart(data);
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId) => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_URL}/api/store/cart/${sessionId}/remove/${productId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      setCart(data);
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const clearCart = async () => {
    if (!window.confirm('Clear entire cart?')) return;
    try {
      const sessionId = getSessionId();
      await fetch(`${API_URL}/api/store/cart/${sessionId}`, { method: 'DELETE' });
      loadCart();
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-cyan-400 font-['Orbitron']">Shopping Cart</h1>
            <p className="text-gray-400 mt-1">{items.length} items in your cart</p>
          </div>
          <Link
            to="/store"
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft size={20} />
            Continue Shopping
          </Link>
        </div>

        {isEmpty ? (
          <div className="text-center py-16 border border-cyan-500/30 rounded-lg">
            <ShoppingBag className="w-24 h-24 text-gray-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-400 mb-2">Your cart is empty</h2>
            <p className="text-gray-500 mb-6">Add some products to get started!</p>
            <Link
              to="/store"
              className="inline-block bg-cyan-500 hover:bg-cyan-400 text-black px-8 py-3 rounded font-bold transition-all"
            >
              Browse Store
            </Link>
          </div>
        ) : (
          <div className="grid gap-6">
            {/* Cart Items */}
            {items.map((item) => (
              <div
                key={item.product_id}
                className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 rounded-lg p-6 flex gap-6"
              >
                {/* Product Image */}
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded flex-shrink-0 flex items-center justify-center">
                  {item.image_url ? (
                    <img
          decoding="async"
          loading="lazy" src={item.image_url} alt={item.product_name} className="w-full h-full object-cover rounded" />
                  ) : (
                    <ShoppingBag className="w-8 h-8 text-cyan-500/50" />
                  )}
                </div>

                {/* Product Info */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-cyan-400 mb-1">{item.product_name}</h3>
                  <p className="text-gray-400 text-sm mb-2">Quantity: {item.quantity}</p>
                  <div className="flex items-center gap-4">
                    <div className="text-lg font-bold text-white">
                      ${item.price_usd * item.quantity}
                    </div>
                    {item.price_crypto && (
                      <div className="text-sm text-gray-500">
                        {item.price_crypto * item.quantity} USDC
                      </div>
                    )}
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeItem(item.product_id)}
                  className="text-red-400 hover:text-red-300 transition-colors"
                  title="Remove from cart"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}

            {/* Cart Summary */}
            <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xl text-gray-300">Subtotal</span>
                <span className="text-2xl font-bold text-white">${cart.total_usd?.toFixed(2)}</span>
              </div>
              {cart.total_crypto > 0 && (
                <div className="flex items-center justify-between mb-4 text-sm">
                  <span className="text-gray-400">Crypto Price</span>
                  <span className="text-cyan-400 font-bold">{cart.total_crypto?.toFixed(2)} USDC</span>
                </div>
              )}
              <div className="flex gap-4">
                <button
                  onClick={clearCart}
                  className="flex-1 bg-red-500/20 border border-red-500 text-red-400 px-6 py-3 rounded hover:bg-red-500/30 transition-all font-bold"
                >
                  Clear Cart
                </button>
                <button
                  onClick={() => navigate('/store/checkout')}
                  className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black px-6 py-3 rounded font-bold transition-all"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
